import { useState, useEffect } from 'react';
import { Download, RefreshCw, TrendingUp, Users, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminClicks() {
  const [stats, setStats] = useState(null);
  const [dailyReport, setDailyReport] = useState([]);
  const [clicksList, setClicksList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showAllVotes, setShowAllVotes] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Buscar estatísticas
      const statsRes = await fetch(`${API_URL}/api/clicks/stats`);
      const statsData = await statsRes.json();
      setStats(statsData);

      // Buscar relatório diário
      const dailyRes = await fetch(`${API_URL}/api/clicks/daily-report`);
      const dailyData = await dailyRes.json();
      setDailyReport(dailyData);

      // Buscar lista de votos
      const listRes = await fetch(`${API_URL}/api/clicks/list`);
      const listData = await listRes.json();
      setClicksList(listData);

      setLastUpdate(new Date().toLocaleString('pt-BR'));
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleExportCSV = () => {
    window.open(`${API_URL}/api/clicks/export`, '_blank');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#002776] to-[#001a4d] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              Painel de Palpites
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Última atualização: {lastUpdate || 'Carregando...'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 bg-[#009C3B] hover:bg-[#007830] text-white px-4 py-2 rounded-lg transition-all"
            >
              <Download className="w-4 h-4" />
              Exportar Excel
            </button>
          </div>
        </div>

        {/* Cards de Estatísticas */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {/* Total */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#FFDF00] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#002776]" />
                </div>
                <span className="text-white/60 text-sm">Total de Palpites</span>
              </div>
              <p className="text-4xl font-bold text-white">{stats.total}</p>
            </div>

            {/* Brasil Ganha */}
            <div className="bg-[#009C3B]/30 backdrop-blur-sm rounded-2xl p-6 border border-[#009C3B]/50">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#009C3B] flex items-center justify-center">
                  <span className="text-xl">🏆</span>
                </div>
                <span className="text-white/80 text-sm">Brasil Ganha</span>
              </div>
              <p className="text-4xl font-bold text-white">{stats.brasil_ganha}</p>
              <p className="text-[#FFDF00] text-sm mt-1">{stats.percentual_ganha}%</p>
            </div>

            {/* Brasil Não Ganha */}
            <div className="bg-red-500/30 backdrop-blur-sm rounded-2xl p-6 border border-red-500/50">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                  <span className="text-xl">😅</span>
                </div>
                <span className="text-white/80 text-sm">Brasil Não Ganha</span>
              </div>
              <p className="text-4xl font-bold text-white">{stats.brasil_perde}</p>
              <p className="text-red-300 text-sm mt-1">{stats.percentual_perde}%</p>
            </div>
          </div>
        )}

        {/* Barra de Progresso Visual */}
        {stats && stats.total > 0 && (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 mb-8">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#FFDF00]" />
              Distribuição dos Palpites
            </h3>
            <div className="h-8 rounded-full overflow-hidden flex">
              <div 
                className="bg-[#009C3B] flex items-center justify-center text-white text-sm font-bold transition-all duration-500"
                style={{ width: `${stats.percentual_ganha}%` }}
              >
                {stats.percentual_ganha > 10 && `${stats.percentual_ganha}%`}
              </div>
              <div 
                className="bg-red-500 flex items-center justify-center text-white text-sm font-bold transition-all duration-500"
                style={{ width: `${stats.percentual_perde}%` }}
              >
                {stats.percentual_perde > 10 && `${stats.percentual_perde}%`}
              </div>
            </div>
            <div className="flex justify-between mt-2 text-sm">
              <span className="text-[#009C3B]">🏆 Brasil Ganha</span>
              <span className="text-red-400">😅 Brasil Não Ganha</span>
            </div>
          </div>
        )}

        {/* Lista de Votos */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold">Todos os Palpites</h3>
            <button
              onClick={() => setShowAllVotes(!showAllVotes)}
              className="text-[#FFDF00] text-sm flex items-center gap-1 hover:underline"
            >
              {showAllVotes ? 'Mostrar menos' : 'Ver todos'}
              {showAllVotes ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          
          {clicksList.length === 0 ? (
            <p className="text-white/60 text-center py-8">
              Nenhum palpite registrado ainda.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left text-white/60 text-sm py-3 px-2">Data/Hora</th>
                    <th className="text-left text-white/60 text-sm py-3 px-2">Nome</th>
                    <th className="text-left text-white/60 text-sm py-3 px-2">WhatsApp</th>
                    <th className="text-left text-white/60 text-sm py-3 px-2">Email</th>
                    <th className="text-center text-white/60 text-sm py-3 px-2">Palpite</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllVotes ? clicksList : clicksList.slice(0, 10)).map((click, index) => (
                    <tr key={index} className="border-b border-white/10 hover:bg-white/5">
                      <td className="text-white/80 py-3 px-2 text-sm">{formatDate(click.timestamp)}</td>
                      <td className="text-white font-medium py-3 px-2">{click.nome || '-'}</td>
                      <td className="text-white/80 py-3 px-2">{click.whatsapp || '-'}</td>
                      <td className="text-white/80 py-3 px-2 text-sm">{click.email || '-'}</td>
                      <td className="text-center py-3 px-2">
                        {click.palpite === 'brasil_ganha' ? (
                          <span className="inline-flex items-center gap-1 bg-[#009C3B]/30 text-[#009C3B] px-2 py-1 rounded-full text-xs font-bold">
                            🏆 Ganha
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-red-500/30 text-red-400 px-2 py-1 rounded-full text-xs font-bold">
                            😅 Não Ganha
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!showAllVotes && clicksList.length > 10 && (
                <p className="text-white/40 text-sm text-center mt-4">
                  Mostrando 10 de {clicksList.length} palpites
                </p>
              )}
            </div>
          )}
        </div>

        {/* Relatório Diário */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
          <h3 className="text-white font-bold mb-4">Resumo por Dia</h3>
          
          {dailyReport.length === 0 ? (
            <p className="text-white/60 text-center py-8">
              Nenhum palpite registrado ainda.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left text-white/60 text-sm py-3 px-2">Data</th>
                    <th className="text-center text-[#009C3B] text-sm py-3 px-2">🏆 Ganha</th>
                    <th className="text-center text-red-400 text-sm py-3 px-2">😅 Não Ganha</th>
                    <th className="text-right text-white/60 text-sm py-3 px-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyReport.map((day, index) => (
                    <tr key={index} className="border-b border-white/10 hover:bg-white/5">
                      <td className="text-white py-3 px-2">{day.date}</td>
                      <td className="text-center text-[#009C3B] font-bold py-3 px-2">{day.brasil_ganha || 0}</td>
                      <td className="text-center text-red-400 font-bold py-3 px-2">{day.brasil_perde || 0}</td>
                      <td className="text-right text-white font-bold py-3 px-2">{day.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-white/40 text-xs mt-8">
          Os dados são atualizados automaticamente a cada 30 segundos
        </p>
      </div>
    </div>
  );
}
